/**
 * Online diagnostic (lead magnet, 28/10/2026): what the public page asks before any AI
 * call. The choices follow the axes of the validated scenario map
 * (implementation-c/skill-des-suites/carte-validee-2026-10-27.json). Shared by the page
 * and the `lead-diagnostic` edge function, which validates the answers again.
 */

export const DIAGNOSTIC_ROLES = [
  { key: 'sdr', label: 'SDR / BDR' },
  { key: 'ae', label: 'Account executive' },
  { key: 'am', label: 'Account manager' },
  { key: 'csm', label: 'Customer success' },
  { key: 'manager', label: 'Manager commercial' },
  { key: 'founder', label: 'Dirigeant qui vend' },
  { key: 'revops', label: 'RevOps' },
  { key: 'marketing', label: 'Marketing' },
] as const;

export const DIAGNOSTIC_TEAM_SIZES = [
  { key: 'solo', label: 'Juste moi' },
  { key: '2-5', label: '2 à 5' },
  { key: '6-15', label: '6 à 15' },
  { key: '16-50', label: '16 à 50' },
  { key: '50+', label: 'Plus de 50' },
] as const;

export const DIAGNOSTIC_CRMS = [
  { key: 'hubspot', label: 'HubSpot' },
  { key: 'salesforce', label: 'Salesforce' },
  { key: 'pipedrive', label: 'Pipedrive' },
  { key: 'other', label: 'Un autre' },
  { key: 'none', label: 'Aucun' },
] as const;

export const DIAGNOSTIC_MAILBOXES = [
  { key: 'google', label: 'Google' },
  { key: 'microsoft', label: 'Microsoft' },
  { key: 'other', label: 'Autre' },
] as const;

export const DIAGNOSTIC_MOTIONS = [
  { key: 'outbound', label: 'Prospection sortante' },
  { key: 'inbound', label: 'Demandes entrantes' },
  { key: 'expansion', label: 'Développement des clients' },
  { key: 'renewal', label: 'Renouvellements' },
  { key: 'partners', label: 'Partenaires et revendeurs' },
] as const;

export const DIAGNOSTIC_AI_USES = [
  { key: 'none', label: 'Pas encore' },
  { key: 'individual', label: 'Chacun de son côté' },
  { key: 'tools', label: 'Des outils en place' },
] as const;

export type DiagnosticRole = (typeof DIAGNOSTIC_ROLES)[number]['key'];
export type DiagnosticTeamSize = (typeof DIAGNOSTIC_TEAM_SIZES)[number]['key'];
export type DiagnosticCrm = (typeof DIAGNOSTIC_CRMS)[number]['key'];
export type DiagnosticMailbox = (typeof DIAGNOSTIC_MAILBOXES)[number]['key'];
export type DiagnosticMotion = (typeof DIAGNOSTIC_MOTIONS)[number]['key'];
export type DiagnosticAiUse = (typeof DIAGNOSTIC_AI_USES)[number]['key'];

/** Where time goes, by role. Shown for the roles present in the team. */
export const DIAGNOSTIC_PAINS: Readonly<Record<DiagnosticRole, ReadonlyArray<{ key: string; label: string }>>> = {
  sdr: [
    { key: 'sdr_targets', label: 'Trouver les comptes qui valent le coup' },
    { key: 'sdr_first_message', label: 'Écrire des premiers messages qui ne sonnent pas génériques' },
    { key: 'sdr_follow_up', label: 'Savoir qui relancer, et quand' },
  ],
  ae: [
    { key: 'ae_meeting_prep', label: 'Préparer les rendez-vous' },
    { key: 'ae_follow_up', label: 'Faire le suivi après un rendez-vous' },
    { key: 'ae_stalled_deals', label: 'Les affaires qui s’enlisent' },
    { key: 'ae_crm_entry', label: 'Tenir le CRM à jour' },
  ],
  am: [
    { key: 'am_expansion', label: 'Repérer les clients à développer' },
    { key: 'am_renewal', label: 'Préparer les renouvellements' },
  ],
  csm: [
    { key: 'csm_churn', label: 'Voir venir les départs' },
    { key: 'csm_reviews', label: 'Préparer les points clients' },
  ],
  manager: [
    { key: 'manager_visibility', label: 'Savoir où en est l’équipe sans tout demander' },
    { key: 'manager_coaching', label: 'Coacher sur les appels' },
    { key: 'manager_forecast', label: 'Prévoir le chiffre' },
  ],
  founder: [
    { key: 'founder_time', label: 'Vendre sans y passer ses soirées' },
    { key: 'founder_method', label: 'Transmettre sa façon de vendre' },
  ],
  revops: [
    { key: 'revops_data', label: 'Des données CRM incomplètes ou en double' },
    { key: 'revops_reporting', label: 'Des rapports faits à la main' },
  ],
  marketing: [
    { key: 'marketing_handoff', label: 'Passer les leads aux commerciaux au bon moment' },
    { key: 'marketing_content', label: 'Des contenus que les commerciaux utilisent vraiment' },
  ],
};

/** Booking link of the site (website/public/index.html): « Contacter Domino ». */
export const DIAGNOSTIC_CONTACT_URL = 'https://cal.com/benoit-arpin-kfupzh/30min';

/** Free text sent to a model later: bounded so a page cannot carry a novel. */
export const DIAGNOSTIC_TEXT_MAX = 1000;

export interface DiagnosticAnswers {
  website: string;
  role: DiagnosticRole;
  teamRoles: DiagnosticRole[];
  teamSize: DiagnosticTeamSize;
  /** Tools: no longer asked since 03/11 (Benoit: not needed for the diagnostic); kept for older answers. */
  crm?: DiagnosticCrm;
  mailbox?: DiagnosticMailbox;
  callsRecorded?: boolean;
  motions: DiagnosticMotion[];
  pains: string[];
  /** Before 03/11: one free text for every role. Still read for older answers. */
  painsOther: string;
  /** Since 03/11: a free text under each role of "Où part le temps ?". */
  painsOtherByRole?: Partial<Record<DiagnosticRole, string>>;
  aiUse: DiagnosticAiUse;
  aiIdeas: string;
  goal: string;
  /** Since 03/11: the problem to solve first, on the objectives screen. */
  problem?: string;
  /** Since 03/11: where the visitor came from (`?source=` or `?utm_source=` of the link), for Benoit's alerts. */
  source?: string;
}

/** A source label kept short and plain: letters, digits, dash, underscore, dot. */
export const DIAGNOSTIC_SOURCE_MAX = 60;
export const cleanDiagnosticSource = (raw: string | null | undefined): string | undefined => {
  const value = (raw ?? '').trim().toLowerCase().replace(/[^a-z0-9._-]/g, '').slice(0, DIAGNOSTIC_SOURCE_MAX);
  return value || undefined;
};

export type DiagnosticIdentityProvider = 'google' | 'microsoft';

export type DiagnosticStatus = 'pending' | 'generating' | 'ready' | 'failed';

export interface DiagnosticView {
  id: string;
  email: string;
  domain: string;
  status: DiagnosticStatus;
  createdAt: string;
  answers: DiagnosticAnswers;
  result: DiagnosticResult | null;
}

export const painsForRoles = (roles: readonly DiagnosticRole[]): ReadonlyArray<{ key: string; label: string; role: DiagnosticRole }> =>
  DIAGNOSTIC_ROLES
    .filter((role) => roles.includes(role.key))
    .flatMap((role) => DIAGNOSTIC_PAINS[role.key].map((pain) => ({ ...pain, role: role.key })));

export const ALL_DIAGNOSTIC_PAIN_KEYS: readonly string[] = Object.values(DIAGNOSTIC_PAINS).flat().map((pain) => pain.key);

/** Throwaway mailboxes: a diagnostic is for a company, and these are how bots sign up. */
export const DISPOSABLE_EMAIL_DOMAINS: readonly string[] = [
  'mailinator.com', 'yopmail.com', 'yopmail.fr', 'guerrillamail.com', 'sharklasers.com',
  '10minutemail.com', 'temp-mail.org', 'tempmail.com', 'trashmail.com', 'dispostable.com',
  'getnada.com', 'maildrop.cc', 'mailnesia.com', 'mohmal.com', 'throwawaymail.com',
];

/**
 * P2: what Domino would do for the team, written by the model in a closed catalogue and
 * checked by the code (capabilities and scenarios known, roles of the team only, the status
 * of each app computed from the tools the prospect named).
 */
export type DiagnosticAppStatus = 'today' | 'with_you';
export type DiagnosticMeasureSource = 'app_list' | 'hubspot_deals' | 'hubspot_meetings';

/** A source that makes an app better without being needed for it to work. */
export interface DiagnosticAppExtra {
  key: string;
  label: string;
  status: DiagnosticAppStatus;
}

export interface DiagnosticApp {
  name: string;
  reads: string;
  produces: string;
  /** Before prompt v5: one sentence. Since v5: `exampleTitle`, `exampleFact`, `exampleDetail`. */
  example: string;
  /** One result line as the app would show it (v5): who or what, a key fact, the detail. */
  exampleTitle?: string;
  exampleFact?: string;
  exampleDetail?: string;
  /** What the app cannot work without: its status is computed on these only. */
  builtFrom: string[];
  /** Absent from diagnostics written before 28/10 (prompt v1). */
  enrichedBy?: DiagnosticAppExtra[];
  /** What the prospect said that this app answers: refs of `diagnosticNeeds` (prompt v7, 03/11). */
  answers?: string[];
  /** How the app would go about it, in order: a plausible hypothesis, not checked (prompt v4). */
  steps?: string[];
  status: DiagnosticAppStatus;
}

export interface DiagnosticChainStep {
  /** Two to four words for the strip of the day (prompt v5). */
  label?: string;
  text: string;
  by: 'domino' | 'person';
  capability: string;
}

export interface DiagnosticRitual {
  name: string;
  cadence: string;
  what: string;
}

export interface DiagnosticRolePlan {
  role: DiagnosticRole;
  headline: string;
  apps: DiagnosticApp[];
  chain: DiagnosticChainStep[];
  rituals: DiagnosticRitual[];
  scenarioIds: string[];
}

export interface DiagnosticObjective {
  text: string;
  measure: string;
  source: DiagnosticMeasureSource | null;
}

/** One block of the page (prompt v9, 03/11): what the prospect wants, then the apps serving it, in order. */
export interface DiagnosticAim {
  /** Two to six words, rephrased by the model. */
  title: string;
  /** Refs of `diagnosticNeeds` this block serves. */
  answers: string[];
  /** App names, in order; each app of the diagnostic is in one block only. */
  apps: string[];
}

export interface DiagnosticResult {
  summary: string;
  roles: DiagnosticRolePlan[];
  /** Absent before prompt v9: the page then groups the apps by role. */
  aims?: DiagnosticAim[];
  objective: DiagnosticObjective | null;
  siteRead: boolean;
  writtenAt: string;
}

/** The free texts of "Où part le temps ?", per role of the team, then the older single text. */
export const painsOtherTexts = (answers: DiagnosticAnswers): Array<{ role: DiagnosticRole | null; text: string }> => [
  ...answers.teamRoles
    .map((role) => ({ role, text: answers.painsOtherByRole?.[role]?.trim() ?? '' }))
    .filter((entry) => entry.text.length > 0),
  ...(answers.painsOther.trim() ? [{ role: null, text: answers.painsOther.trim() }] : []),
];

export interface DiagnosticNeedRef {
  /** `goal`, `problem`, a pain key, `other:<role>` or `other` (the older single text). */
  ref: string;
  role: DiagnosticRole | null;
  /** The prospect's own words, or the label of the pain they ticked. */
  label: string;
}

/**
 * What the prospect expects, each with a stable ref (03/11): every one must be answered by an
 * app of the diagnostic; the page shows on each app the ones it answers.
 */
export const diagnosticNeeds = (answers: DiagnosticAnswers): DiagnosticNeedRef[] => [
  ...(answers.goal.trim() ? [{ ref: 'goal', role: null, label: answers.goal.trim() }] : []),
  ...(answers.problem?.trim() ? [{ ref: 'problem', role: null, label: answers.problem.trim() }] : []),
  ...painsForRoles(answers.teamRoles)
    .filter((pain) => answers.pains.includes(pain.key))
    .map((pain) => ({ ref: pain.key, role: pain.role, label: pain.label })),
  ...painsOtherTexts(answers).map((entry) => ({ ref: entry.role ? `other:${entry.role}` : 'other', role: entry.role, label: entry.text })),
];
